import React, { useMemo } from "react";
import {
  FaArrowDown,
  FaArrowUp,
  FaCheck,
  FaCopy,
  FaEdit,
  FaPowerOff,
  FaRecycle,
  FaTrash,
  FaUserTie,
  FaMapMarkerAlt,
  FaLock,
  FaLockOpen,
} from "react-icons/fa";

import {
  formatEuroValue,
  getDojosById,
  getGroupSchedules,
  getGroupTeachers,
  getQuotaBadgeMeta,
  getQuotaMeta,
  toBool,
} from "../utils/groupsSettingsUtils";

function ActionButton({
  title,
  onClick,
  disabled,
  children,
  tone = "default",
}) {
  const tones = {
    default:
      "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    primary:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    success:
      "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    warn: "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    danger:
      "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border transition disabled:cursor-not-allowed disabled:opacity-50 ${
        tones[tone] || tones.default
      }`}
    >
      {children}
    </button>
  );
}

function SkeletonRows() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-4 sm:p-5 lg:p-6">
        {[...Array(6)].map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          <FaUserTie />
        </div>

        <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
          Aucun groupe
        </h3>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Créez un groupe ou modifiez vos filtres.
        </p>
      </div>
    </section>
  );
}

function QuotaCell({ group, isQuotaActive }) {
  const quota = getQuotaMeta(group);
  const badge = getQuotaBadgeMeta(group, isQuotaActive);

  return (
    <div className="min-w-[170px]">
      <span
        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${badge.className}`}
      >
        {badge.label}
      </span>

      <div className="mt-2 flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
        <span>
          {quota.members}
          {quota.hasQuota ? ` / ${quota.quota}` : " adh."}
        </span>

        {quota.hasQuota ? <span>{quota.percent}%</span> : null}
      </div>

      {quota.hasQuota ? (
        <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
          <div
            className={`h-full rounded-full transition-all ${
              quota.isFull
                ? "bg-rose-500"
                : quota.isAlmostFull
                  ? "bg-amber-500"
                  : "bg-emerald-500"
            }`}
            style={{ width: `${quota.percent}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}

function ScheduleList({ schedules }) {
  if (!schedules.length) {
    return (
      <span className="text-slate-400 dark:text-slate-500">Aucun horaire</span>
    );
  }

  return (
    <div className="space-y-1">
      {schedules.slice(0, 3).map((schedule, index) => (
        <span
          key={`${schedule}-${index}`}
          className="block rounded-xl bg-slate-100 px-2 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          {schedule}
        </span>
      ))}

      {schedules.length > 3 ? (
        <span className="block text-xs font-bold text-slate-500 dark:text-slate-400">
          +{schedules.length - 3} horaire(s)
        </span>
      ) : null}
    </div>
  );
}

export default function GroupsTable({
  groups,
  dojos,
  loading,
  isQuotaActive,
  deletingId,
  clearingId,
  activatingId,
  activatingInscriptionId,
  reordering,
  onEdit,
  onDuplicate,
  onDelete,
  onClearMembers,
  onToggleActive,
  onToggleInscription,
  onMoveUp,
  onMoveDown,
}) {
  const dojosById = useMemo(() => getDojosById(dojos), [dojos]);

  if (loading) return <SkeletonRows />;
  if (!groups.length) return <EmptyState />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Liste
          </p>

          <h3 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            Groupes enregistrés
          </h3>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Le dojo est maintenant affiché directement sous le nom du groupe
            pour gagner de la place dans le tableau.
          </p>
        </div>

        <div className="overflow-x-auto rounded-[22px] border border-slate-200 dark:border-slate-800">
          <table className="w-full min-w-[1400px]">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Ordre</th>
                <th className="px-4 py-3 text-left font-bold">Groupe</th>
                <th className="px-4 py-3 text-left font-bold">Statut</th>
                <th className="px-4 py-3 text-left font-bold">Inscription</th>
                <th className="px-4 py-3 text-left font-bold">Quota</th>
                <th className="px-4 py-3 text-left font-bold">Horaires</th>
                <th className="px-4 py-3 text-left font-bold">Âges / tarifs</th>
                <th className="px-4 py-3 text-left font-bold">Enseignants</th>
                <th className="sticky right-0 z-20 border-l border-slate-200 bg-slate-50 px-4 py-3 text-right font-bold dark:border-slate-700 dark:bg-slate-950">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {groups.map((group, index) => {
                const schedules = getGroupSchedules(group);
                const teachers = getGroupTeachers(group);
                const dojo = dojosById[String(group.dojo_id)];
                const active = toBool(group.is_active);
                const inscriptionActive =
                  group.inscription_active === undefined
                    ? true
                    : toBool(group.inscription_active);
                const quota = getQuotaMeta(group);

                const rowTone = !active
                  ? "bg-slate-50/80 dark:bg-slate-950/50"
                  : quota.isFull && isQuotaActive
                    ? "bg-rose-50/70 dark:bg-rose-500/5"
                    : quota.isAlmostFull
                      ? "bg-amber-50/60 dark:bg-amber-500/5"
                      : "bg-white dark:bg-slate-900";

                return (
                  <tr
                    key={group.id}
                    className={`transition hover:bg-slate-50 dark:hover:bg-slate-800/50 ${rowTone}`}
                  >
                    <td className="px-4 py-4 align-top">
                      <div className="flex items-center gap-2">
                        <span className="min-w-8 rounded-xl bg-white px-2 py-1 text-center text-xs font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700">
                          {group.ordre_groupe || index + 1}
                        </span>

                        <div className="flex flex-col">
                          <button
                            type="button"
                            onClick={() => onMoveUp(group)}
                            disabled={index === 0 || reordering}
                            className="rounded-md p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-slate-800 dark:hover:text-white"
                            title="Monter"
                          >
                            <FaArrowUp className="text-[11px]" />
                          </button>

                          <button
                            type="button"
                            onClick={() => onMoveDown(group)}
                            disabled={index === groups.length - 1 || reordering}
                            className="rounded-md p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-slate-800 dark:hover:text-white"
                            title="Descendre"
                          >
                            <FaArrowDown className="text-[11px]" />
                          </button>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <div className="flex items-start gap-3">
                        <span
                          className={`mt-1 h-4 w-4 shrink-0 rounded-full ring-2 ring-white dark:ring-slate-900 ${
                            group.color_groupe || "bg-slate-400"
                          }`}
                        />

                        <div className="min-w-0">
                          <p className="font-bold text-slate-900 dark:text-white">
                            {group.nom || "Sans nom"}
                          </p>

                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs leading-5">
                            <span className="inline-flex items-center gap-1 text-slate-500 dark:text-slate-400">
                              <FaMapMarkerAlt className="text-[10px]" />
                              {dojo?.dojo_nom || "Dojo non renseigné"}
                            </span>

                            {group.discipline_groupe ? (
                              <span className="font-bold text-indigo-700 dark:text-indigo-300">
                                {group.discipline_groupe}
                              </span>
                            ) : (
                              <span className="text-slate-400 dark:text-slate-500">
                                Discipline non renseignée
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <button
                        type="button"
                        onClick={() => onToggleActive(group)}
                        disabled={activatingId === group.id}
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ring-1 transition disabled:cursor-not-allowed disabled:opacity-60 ${
                          active
                            ? "bg-emerald-100 text-emerald-700 ring-emerald-200 hover:bg-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                            : "bg-rose-100 text-rose-700 ring-rose-200 hover:bg-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20"
                        }`}
                      >
                        {active ? <FaCheck /> : <FaPowerOff />}
                        {active ? "Actif" : "Inactif"}
                      </button>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <button
                        type="button"
                        onClick={() => onToggleInscription(group)}
                        disabled={activatingInscriptionId === group.id}
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ring-1 transition disabled:cursor-not-allowed disabled:opacity-60 ${
                          inscriptionActive
                            ? "bg-emerald-100 text-emerald-700 ring-emerald-200 hover:bg-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                            : "bg-amber-100 text-amber-700 ring-amber-200 hover:bg-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20"
                        }`}
                        title={
                          inscriptionActive
                            ? "Fermer les inscriptions sur ce groupe"
                            : "Ouvrir les inscriptions sur ce groupe"
                        }
                      >
                        {inscriptionActive ? <FaLockOpen /> : <FaLock />}
                        {inscriptionActive ? "Ouverte" : "Fermée"}
                      </button>

                      {!active && inscriptionActive ? (
                        <p className="mt-2 max-w-[160px] text-xs leading-5 text-slate-500 dark:text-slate-400">
                          Le groupe est ouvert à l’inscription mais masqué
                          publiquement.
                        </p>
                      ) : null}
                    </td>

                    <td className="px-4 py-4 align-top">
                      <QuotaCell group={group} isQuotaActive={isQuotaActive} />
                    </td>

                    <td className="px-4 py-4 align-top">
                      <ScheduleList schedules={schedules} />
                    </td>

                    <td className="px-4 py-4 align-top">
                      <div className="space-y-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                        <p>
                          <span className="font-bold text-slate-800 dark:text-slate-100">
                            Âges :
                          </span>{" "}
                          {group.annees_naissance || "—"}
                        </p>

                        <p>
                          Licence :{" "}
                          <span className="font-bold">
                            {formatEuroValue(group.tarif_licence)}
                          </span>
                        </p>

                        <p>
                          Cotisation :{" "}
                          <span className="font-bold">
                            {formatEuroValue(group.tarif_cotisation)}
                          </span>
                        </p>
                      </div>
                    </td>

                    <td className="px-4 py-4 align-top">
                      {teachers.length ? (
                        <div className="space-y-1">
                          {teachers.slice(0, 4).map((teacher, teacherIndex) => (
                            <span
                              key={`${teacher}-${teacherIndex}`}
                              className="block text-xs font-bold text-slate-700 dark:text-slate-200"
                            >
                              {teacher}
                            </span>
                          ))}

                          {teachers.length > 4 ? (
                            <span className="block text-xs font-bold text-slate-500 dark:text-slate-400">
                              +{teachers.length - 4}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 dark:text-slate-500">
                          Aucun enseignant
                        </span>
                      )}
                    </td>

                    <td
                      className={`sticky right-0 z-10 border-l border-slate-200 px-4 py-4 align-top dark:border-slate-700 ${rowTone}`}
                    >
                      <div className="flex items-center justify-end gap-2">
                        <ActionButton
                          title="Modifier"
                          onClick={() => onEdit(group)}
                          tone="primary"
                        >
                          <FaEdit />
                        </ActionButton>

                        <ActionButton
                          title="Dupliquer"
                          onClick={() => onDuplicate(group)}
                          tone="default"
                        >
                          <FaCopy />
                        </ActionButton>

                        <ActionButton
                          title="Vider le groupe"
                          onClick={() => onClearMembers(group)}
                          disabled={clearingId === group.id}
                          tone="warn"
                        >
                          <FaRecycle />
                        </ActionButton>

                        <ActionButton
                          title="Supprimer"
                          onClick={() => onDelete(group)}
                          disabled={deletingId === group.id}
                          tone="danger"
                        >
                          <FaTrash />
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
